import type { HealthRecord } from './aqueduct/engine/workHealth';
import * as Sentry from '@sentry/browser';

export type ConsoleContext = {
	origin: string;
	path: string;
	sessionId: string;
	buildId: string;
};

export type TelemetryUser = { id: string; email?: string };
type TelemetryLevel = 'info' | 'warn' | 'error';
let telemetryUser: TelemetryUser | null = null;
const userSubscribers = new Set<(user: TelemetryUser | null) => void>();

export function setTelemetryUser(user: TelemetryUser | null): void {
	if (telemetryUser?.id === user?.id && telemetryUser?.email === user?.email) return;
	for (const subscriber of userSubscribers) subscriber(user);
	telemetryUser = user ? { ...user } : null;
	if (initialized) {
		Sentry.setUser(telemetryUser);
		Sentry.logger.info('Diagnostic identity updated');
	}
}

export function subscribeTelemetryUser(callback: (user: TelemetryUser | null) => void): () => void {
	userSubscribers.add(callback);
	callback(telemetryUser);
	return () => {
		userSubscribers.delete(callback);
	};
}

/** Project context is owned by an instance, never a global current-project scope. */
export class ProjectTelemetry {
	constructor(
		readonly instanceId: number,
		public documentId: string | null = null
	) {}

	log(level: TelemetryLevel, message: string): void {
		const attributes: Record<string, string | number> = {
			'aqueduct.project_instance_id': this.instanceId,
			...(this.documentId ? { 'aqueduct.document_id': this.documentId } : {})
		};
		// The console integration extracts object-first arguments as log attributes.
		console[level](attributes, message);
		if (level === 'error' && initialized) {
			Sentry.captureMessage(redact(message), { level: 'error', tags: attributes });
		}
	}
}

/** The health observer has one emitter, on the page. Bypass console capture so
 * one problem is exactly one structured log plus one issue. */
export function emitHealthRecord(record: HealthRecord): void {
	if (!initialized) return;
	const attributes: Record<string, string | number | boolean> = Object.fromEntries(
		Object.entries(record.attributes).map(([key, value]) => [
			key,
			typeof value === 'string' ? redact(value) : value
		])
	);
	Sentry.logger[record.level](record.name, attributes);
	if (record.name !== 'aqueduct.health.problem') return;
	const level: 'warning' | 'error' = record.level === 'warn' ? 'warning' : 'error';
	const context = {
		level,
		fingerprint: [
			'aqueduct.health',
			String(attributes['diagnostic.trigger']),
			String(attributes['diagnostic.reason']),
			String(attributes['diagnostic.operation_kind'])
		],
		tags: Object.fromEntries(
			Object.entries(attributes).filter(([key]) => key !== 'diagnostic.snapshot_json')
		),
		extra: { evidence: attributes['diagnostic.snapshot_json'] }
	};
	if (record.error) {
		const error: Error = new Error(redact(record.error.message).slice(0, 256));
		error.name = record.error.name.slice(0, 64);
		error.stack = redact(record.error.stack ?? '').slice(0, 1024);
		Sentry.captureException(error, context);
	} else Sentry.captureMessage(record.name, context);
}

type StartupEntry = {
	level: 'debug' | 'info' | 'log' | 'warn' | 'error' | 'trace' | 'assert';
	message: string;
	timestamp: number;
};

type StartupConsole = {
	entries: StartupEntry[];
	dropped: number;
	active: boolean;
};

declare global {
	var __aqueductStartupConsole: StartupConsole | undefined;
	var __aqueductDiagnosticSessionId: string | undefined;
}

const DSN: string =
	'https://930eb036c1ae460abf184f0a18c752dc@o4505491827916800.ingest.us.sentry.io/4505491831652352';
const FLUSH_TIMEOUT_MS: number = 2000;
let initialized: boolean = false;

export function consoleLogsAllowed(origin: string): boolean {
	return origin === 'https://aqueduct.sh';
}

export function pageConsoleContext(buildId: string): ConsoleContext {
	return {
		origin: location.origin,
		path: location.pathname,
		sessionId: (globalThis.__aqueductDiagnosticSessionId ??= crypto.randomUUID()),
		buildId
	};
}

// Redact credentials rather than filtering messages or console levels.
function redact(text: string): string {
	return text
		.replace(/Bearer\s+[^\s"']+/gi, 'Bearer [Redacted]')
		.replace(/eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g, '[Redacted JWT]')
		.replace(
			/("[^"\n]*(?:password|secret|authorization|token)[^"\n]*"\s*:\s*")[^"]*"/gi,
			'$1[Redacted]"'
		)
		.replace(/([?&](?:token|key|code|secret|password)=)[^&#\s"']+/gi, '$1[Redacted]');
}

export function initializeSentryConsole(context: ConsoleContext, source: 'page' | 'worker'): void {
	if (initialized || !consoleLogsAllowed(context.origin)) return;
	initialized = true;
	Sentry.init({
		dsn: DSN,
		environment: 'production',
		release: context.buildId,
		enableLogs: true,
		sendDefaultPii: false,
		// Workers have no DOM. Keep their setup explicitly limited to console logs.
		...(source === 'worker' ? { defaultIntegrations: false } : {}),
		integrations: [Sentry.consoleLoggingIntegration()],
		initialScope: {
			tags: {
				'diagnostic.session_id': context.sessionId,
				'app.build_id': context.buildId,
				'console.source': source
			}
		},
		beforeSendLog(log) {
			if (!consoleLogsAllowed(context.origin)) return null;
			log.message = redact(log.message);
			const attributes: NonNullable<typeof log.attributes> = (log.attributes ??= {});
			for (const [key, value] of Object.entries(attributes)) {
				if (/password|secret|authorization|token/i.test(key)) {
					attributes[key] = '[Redacted]';
				} else if (typeof value === 'string') {
					attributes[key] = redact(value);
				}
			}
			log.attributes = {
				...attributes,
				'diagnostic.session_id': context.sessionId,
				'app.build_id': context.buildId,
				'console.source': source,
				'page.origin': context.origin,
				'page.path_at_boot': context.path,
				...(source === 'page' ? { 'page.path': location.pathname } : {}),
				'browser.user_agent': navigator.userAgent
			};
			return log;
		}
	});
	const startup: StartupConsole | undefined = globalThis.__aqueductStartupConsole;
	if (startup) {
		startup.active = false;
		for (const entry of startup.entries.splice(0)) {
			const level: 'debug' | 'info' | 'warn' | 'error' | 'trace' =
				entry.level === 'log' ? 'info' : entry.level === 'assert' ? 'error' : entry.level;
			Sentry.logger[level](entry.message, {
				'console.startup': true,
				'console.original_timestamp_ms': entry.timestamp
			});
		}
		if (startup.dropped > 0) {
			Sentry.logger.warn('Startup console buffer overflow', { dropped: startup.dropped });
		}
	}
	Sentry.setUser(telemetryUser);
	Sentry.logger.info('Console capture initialized');
	if (source === 'page') {
		// Flush page logs on navigation as well as the SDK's visibility handler.
		addEventListener('pagehide', flushSentryConsole);
	}
}

export function flushSentryConsole(): void {
	if (initialized) void Sentry.flush(FLUSH_TIMEOUT_MS);
}
